import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useUrlSearchParams } from 'use-url-search-params';
import { SORTING_ORDER } from '../../../../constants/index';
import moment from 'moment';
import {
  fetchServiceOrdersLists,
  getAreaList,
  getTechniciansList,
} from '../actions/action';
import { Table, Row, Col, Spinner, Badge } from 'reactstrap';
import 'react-drawer/lib/react-drawer.css';
import DHPagination from '../../../../components/Pagination';
import FilterSearchQueue from '../../../../components/FilterSearch/FilterSearchQueue';
import ServiceOrdersMapView from './ServiceOrdersMapView';
import './ComplaintsList.css';

const MAP_VIEW_LIMIT = 5000;

const ManageServiceOrdersList = (props) => {
  const {
    stores,
    refreshList,
    viewType = 'list',
    filterDrawerOpen,
    setFilterDrawerOpen,
    headerActions,
  } = props;
  const location = useLocation();
  const dispatch = useDispatch();
  const [paginationValue, setPaginationValue] = useUrlSearchParams({ page: 0 });
  const [limitValue, setLimitValue] = useUrlSearchParams({ limit: 10 });
  const [sortingValue, setSortingValue] = useUrlSearchParams({
    sort: '_date',
    order: SORTING_ORDER.ASC,
  });
  const { country = [] } = useSelector((state) => state.country);
  let currentCountry = localStorage.getItem('WEBSITE_COUNTRY');
  const [advancedFilter, setAdvancedFilter] = useState({});
  const [searchFilter, setSearchFilter] = useUrlSearchParams({ search: '' });
  const [statusFilter, setStatusFilter] = useUrlSearchParams({ status: 'All' });
  const [emirate, setEmirate] = useState('');
  const [area, setArea] = useState('');
  const [serviceType, setServiceType] = useState('');
  const [isAmc, setIsAmc] = useState('All');
  const [technician, setTechnician] = useState('');
  // 'All' = no filter, 'with'/'without' = feedback submitted or not,
  // '1'-'5' = exact star rating. Kept in the URL so a filtered list stays
  // shareable/bookmarkable.
  const [feedbackParam, setFeedbackParam] = useUrlSearchParams({
    feedback: 'All',
  });
  const feedbackFilter = String(feedbackParam?.feedback ?? 'All');
  const setFeedbackFilter = (value) => setFeedbackParam({ feedback: value });
  const isRatingFilter = /^[1-5]$/.test(feedbackFilter);

  const SERVICE_TYPE_OPTIONS = [
    'filter_change',
    'water_leakage',
    'installation',
    'reinstallation',
    'amc',
    'others',
  ];

  const [type, setType] = useState('purfier');
  const [store, setStore] = useState('');
  const [storeType, setStoreType] = useState('all');
  const [allAreas, setAllAreas] = useState([]);

  const fetchServiceOrdersList = useCallback(async (data) => {
    try {
      setDataLoading(true);
      await fetchServiceOrdersLists({
        params: data,
        dispatch,
      });
    } catch (error) {
      setDataLoading(false);
      console.error('Error fetching orders:', error);
    } finally {
      setDataLoading(false);
    }
  }, []);

  const fetchAreaList = useCallback(async () => {
    try {
      const response = await getAreaList();
      // Handle both { data: [...] } and directly [...] formats
      const areas = Array.isArray(response) ? response : response?.data || [];
      setAllAreas(areas);
    } catch (error) {
      console.error('Error fetching area list:', error);
    }
  }, []);

  useEffect(() => {
    fetchAreaList();
    getTechniciansList({ dispatch });
  }, [fetchAreaList, dispatch]);

  const { techniciansList } = useSelector((state) => state.technician);
  const technicianOptions = (techniciansList?.technicians || []).map((t) => ({
    value: t.emp_id,
    label:
      `${t.technician_name || ''}${t.emp_id ? ` (${t.emp_id})` : ''}`.trim(),
  }));

  useEffect(() => {
    const delay = setTimeout(() => {
      const isMap = viewType === 'map';
      const hasDateRange = !!(advancedFilter?.frmDt && advancedFilter?.toDt);
      const defaultFrm = moment().subtract(29, 'days').format('YYYY-MM-DD');
      const defaultTo = moment().format('YYYY-MM-DD');
      const effectiveAdvancedFilter =
        isMap && !hasDateRange
          ? { ...advancedFilter, frmDt: defaultFrm, toDt: defaultTo }
          : advancedFilter;
      fetchServiceOrdersList({
        start: isMap ? 0 : Number(paginationValue.page * limitValue.limit),
        limit: isMap ? MAP_VIEW_LIMIT : Number(limitValue.limit),
        sort: { value: sortingValue.sort, order: Number(sortingValue.order) },
        search_qry: searchFilter?.search,
        status: statusFilter?.status === 'All' ? '' : statusFilter?.status,
        advancedFilter: effectiveAdvancedFilter,
        country: currentCountry,
        stores,
        emirate,
        area,
        service_type: serviceType,
        is_amc: isAmc === 'All' ? '' : isAmc,
        technician_id: technician,
        has_feedback:
          feedbackFilter === 'with' ? '1' : feedbackFilter === 'without' ? '0' : '',
        feedback_rating: isRatingFilter ? feedbackFilter : '',
      });
    }, 300);

    return () => clearTimeout(delay);
  }, [
    paginationValue.page,
    limitValue,
    advancedFilter,
    searchFilter,
    statusFilter,
    stores,
    refreshList, // <-- add this dependency
    emirate,
    area,
    serviceType,
    isAmc,
    technician,
    feedbackFilter,
    viewType,
  ]);

  const { serviceOrdersList = [], isLoading } = useSelector(
    (state) => state.complaints,
  );

  const [isDataLoading, setDataLoading] = useState(false);
  const { listOrders } = serviceOrdersList;
  const serviceOrderData = listOrders?.data;

  const areaOptions = Array.from(
    new Set(
      (allAreas?.length > 0 ? allAreas : [])
        .map((area) =>
          typeof area === 'string' ? area : area.name || area.area,
        )
        .filter((a) => a && a !== '-'),
    ),
  ).sort();

  let UserRoleID = '';
  let UserCountry = '';

  if (localStorage.getItem('authUser')) {
    const userObj = JSON.parse(localStorage.getItem('authUser'));
    if (userObj?.role_id !== undefined) {
      UserRoleID = userObj?.role_id;
      UserCountry = userObj?.country;
    }
  }

  let pageIndex =
    Number(serviceOrderData?.length) > 0
      ? Number(paginationValue.page * limitValue.limit)
      : 0;

  const [viewMode, setViewMode] = useState(false);

  const customStyles = `
  .refundOrder .react-drawer-drawer {
    width: ${viewMode ? '50%' : ''} !important;
  }
`;

  return (
    <>
      <>
        <style>{customStyles}</style>
        <Row>
          <Col xl={12}>
            <FilterSearchQueue
              type={'purifier'}
              setAdvancedFilter={setAdvancedFilter}
              setSearchFilter={setSearchFilter}
              setStatusFilter={setStatusFilter}
              setEmirate={setEmirate}
              setArea={setArea}
              emirate={emirate}
              area={area}
              areaOptions={areaOptions}
              serviceType={serviceType}
              setServiceType={setServiceType}
              serviceTypeOptions={SERVICE_TYPE_OPTIONS}
              isAmc={isAmc}
              setIsAmc={setIsAmc}
              technician={technician}
              setTechnician={setTechnician}
              technicianOptions={technicianOptions}
              feedbackFilter={feedbackFilter}
              setFeedbackFilter={setFeedbackFilter}
              feedbackCounts={listOrders?.feedbackCounts}
              externalFilterDrawerOpen={filterDrawerOpen}
              setExternalFilterDrawerOpen={setFilterDrawerOpen}
              headerActions={headerActions}
            />
            {viewType === 'map' ? (
              <ServiceOrdersMapView
                orders={serviceOrderData || []}
                isLoading={isLoading || isDataLoading}
                dateLabel={(() => {
                  const f = advancedFilter?.frmDt;
                  const t = advancedFilter?.toDt;
                  if (f && t) {
                    return f === t
                      ? moment(f).format('Do MMM, YYYY')
                      : `${moment(f).format('Do MMM')} – ${moment(t).format('Do MMM, YYYY')}`;
                  }
                  const from = moment().subtract(29, 'days').format('Do MMM');
                  const to = moment().format('Do MMM, YYYY');
                  return `Last 30 Days (${from} – ${to})`;
                })()}
                isDefaultDate={!(advancedFilter?.frmDt && advancedFilter?.toDt)}
              />
            ) : (
              <div className=" dt-table">
                <div className="table-responsive">
                  <Table className="OrderListTable ServiceOrderTable table mb-0">
                    <thead>
                      <tr>
                        <th nowrap="nowrap">Service Order #</th>
                        <th nowrap="nowrap">Customer</th>
                        <th nowrap="nowrap">Customer Address</th>
                        <th nowrap="nowrap">Area</th>
                        <th nowrap="nowrap">Service Type</th>
                        <th nowrap="nowrap">Amount</th>
                        <th nowrap="nowrap">Status</th>
                        <th nowrap="nowrap">Assigned To</th>
                        <th nowrap="nowrap">Created By</th>
                        <th nowrap="nowrap">Action</th>
                      </tr>
                    </thead>
                    {isLoading || isDataLoading ? (
                      <>
                        <Spinner className="ms-2 sploader" color="primary" />
                        <tbody>
                          {[...Array(4)].map((_, index) => (
                            <tr key={`skeleton-${index}`}>
                              <td>
                                <p className="placeholder-glow">
                                  <span className="placeholder col-12"></span>
                                  <span className="placeholder col-6"></span>
                                  <span
                                    className="placeholder"
                                    style={{ width: '25%' }}
                                  ></span>
                                  <span className="placeholder w-75"></span>
                                </p>
                              </td>
                              <td>
                                <p className="placeholder-glow d-flex flex-column gap-1">
                                  <span className="placeholder col-6"></span>
                                </p>
                              </td>
                              <td>
                                <p className="placeholder-glow">
                                  <span className="placeholder col-6"></span>
                                </p>
                              </td>
                              <td>
                                <p className="placeholder-glow">
                                  <span className="placeholder col-6"></span>
                                </p>
                              </td>
                              <td>
                                <p className="placeholder-glow">
                                  <span className="placeholder col-6"></span>
                                </p>
                              </td>
                              <td>
                                <p className="placeholder-glow d-flex flex-column gap-1">
                                  <span className="placeholder col-8"></span>
                                  <span className="placeholder col-6"></span>
                                  <span className="placeholder col-7"></span>
                                </p>
                              </td>
                              <td>
                                <p className="placeholder-glow">
                                  <span className="placeholder col-6"></span>
                                </p>
                              </td>
                              <td>
                                <p className="placeholder-glow">
                                  <span className="placeholder col-6"></span>
                                  <span
                                    className="placeholder"
                                    style={{ width: '25%' }}
                                  ></span>
                                </p>
                              </td>
                              <td>
                                <p className="placeholder-glow">
                                  <span className="placeholder col-6"></span>
                                  <span
                                    className="placeholder"
                                    style={{ width: '25%' }}
                                  ></span>
                                </p>
                              </td>
                              <td>
                                <p className="placeholder-glow">
                                  <span className="placeholder col-6"></span>
                                  <span
                                    className="placeholder"
                                    style={{ width: '25%' }}
                                  ></span>
                                </p>
                              </td>
                              <td>
                                <p className="placeholder-glow">
                                  <span className="placeholder col-6"></span>
                                  <span
                                    className="placeholder"
                                    style={{ width: '25%' }}
                                  ></span>
                                </p>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </>
                    ) : serviceOrderData?.length > 0 ? (
                      <>
                        {(serviceOrderData || []).map((serviceOrder, index) => {
                          pageIndex++;
                          let cdate = serviceOrder.created_at,
                            orderDt = moment(new Date(cdate)).format(
                              'Do MMM, YYYY',
                            ),
                            orderTime = moment(new Date(cdate)).format(
                              'h:mm:ss a',
                            );
                          let resolvedDate = serviceOrder.resolved_at;
                          const resolvedDateTime = resolvedDate
                            ? moment(new Date(resolvedDate)).format(
                                'Do MMM, YYYY',
                              )
                            : null;
                          const lastScheduledData =
                            serviceOrder?.last_scheduled_date;
                          const lastScheduledDateTime = lastScheduledData
                            ? moment(new Date(lastScheduledData)).format(
                                'Do MMM, YYYY',
                              )
                            : null;
                          const rescheduledData =
                            serviceOrder?.rescheduled_date;
                          const rescheduledDateTime = rescheduledData
                            ? moment(new Date(rescheduledData)).format(
                                'Do MMM, YYYY',
                              )
                            : null;
                          return (
                            <tbody key={serviceOrder?.complaint_no || index}>
                              <tr>
                                <td>
                                  <p className="fs-12 mb-0 d-flex align-items-center gap-2">
                                    <b
                                      style={{
                                        cursor: 'pointer',
                                        color: '#0d6efd',
                                      }}
                                      title="View Complaint Info"
                                      onClick={() => {
                                        if (serviceOrder?.complaint_no) {
                                          window.open(
                                            `/service-orders/manage-service-orders/${serviceOrder.complaint_no}`,
                                            '_blank',
                                          );
                                        }
                                      }}
                                    >
                                      {serviceOrder?.complaint_no}
                                    </b>
                                    {serviceOrder?.source &&
                                      (() => {
                                        const src = String(
                                          serviceOrder.source,
                                        ).toLowerCase();
                                        const sourceStyle =
                                          {
                                            crm: 'bg-soft-pink text-pink',
                                            oms: 'bg-soft-info text-info',
                                            hexa: 'bg-soft-warning text-warning',
                                            web: 'bg-soft-success text-success',
                                            csd: 'bg-soft-dark text-dark',
                                          }[src] ||
                                          'bg-soft-secondary text-secondary';
                                        return (
                                          <span
                                            className={`badge ${sourceStyle} text-uppercase`}
                                            title={`Source: ${serviceOrder.source}`}
                                          >
                                            {serviceOrder.source}
                                          </span>
                                        );
                                      })()}
                                    {serviceOrder?.store && (
                                      <span
                                        className="badge bg-soft-light text-dark border border-light text-uppercase"
                                        title={`Store: ${serviceOrder.store}`}
                                      >
                                        {serviceOrder.store}
                                      </span>
                                    )}
                                  </p>
                                  <p className="fs-12 mb-0 mt-0">
                                    {orderDt} {orderTime}
                                  </p>
                                  {serviceOrder?.ls_order_no && (
                                    <p className="fs-10 mb-0 mt-1">
                                      <b>LS Order No :</b>{' '}
                                      {serviceOrder?.ls_order_no}
                                    </p>
                                  )}
                                  {serviceOrder?.service_order_no && (
                                    <p className="fs-10 mb-0 mt-1">
                                      <b>Service Order No :</b>{' '}
                                      {serviceOrder?.service_order_no}
                                    </p>
                                  )}
                                  {serviceOrder?.contract_receipt_no && (
                                    <p className="fs-10 mb-0 mt-1">
                                      <b>Contract Receipt No :</b>{' '}
                                      {serviceOrder?.contract_receipt_no}
                                    </p>
                                  )}
                                  {String(
                                    serviceOrder?.is_delivery_and_installation_sameday ??
                                      '0',
                                  ) === '1' && (
                                    <span
                                      className="badge bg-soft-success text-success mt-1"
                                      title="Delivery & Installation on the same day"
                                    >
                                      <i className="mdi mdi-truck-fast-outline me-1"></i>
                                      Same-Day Delivery & Installation
                                    </span>
                                  )}
                                  {Number(serviceOrder?.feedback_rating) > 0 &&
                                    (() => {
                                      const rating = Number(
                                        serviceOrder.feedback_rating,
                                      );
                                      const ratingColor =
                                        rating < 3
                                          ? '#dc3545'
                                          : rating === 3
                                            ? '#f5b301'
                                            : '#28a745';
                                      return (
                                        <p
                                          className="fs-12 mb-0 mt-1 d-flex align-items-center"
                                          title={`Feedback Rating: ${serviceOrder.feedback_rating} / 5`}
                                        >
                                          {[1, 2, 3, 4, 5].map((star) => (
                                            <i
                                              key={star}
                                              className={`mdi ${
                                                star <= Math.round(rating)
                                                  ? 'mdi-star'
                                                  : 'mdi-star-outline'
                                              }`}
                                              style={{ color: ratingColor }}
                                            ></i>
                                          ))}
                                          <span
                                            className="ms-1"
                                            style={{ color: ratingColor }}
                                          >
                                            ({serviceOrder.feedback_rating})
                                          </span>
                                        </p>
                                      );
                                    })()}
                                </td>

                                <td>
                                  <span className="fs-12">
                                    {serviceOrder?.customer_name}
                                  </span>
                                  <p className="fs-12 mb-0">
                                    {serviceOrder?.mobile}
                                    <br />
                                    {serviceOrder?.alternate_mobile && (
                                      <>
                                        Alt: {serviceOrder?.alternate_mobile}
                                        <br />
                                      </>
                                    )}
                                    {serviceOrder?.email}
                                  </p>
                                  {(serviceOrder?.company_name || serviceOrder?.trn_no) && (
                                    <p className="fs-12 text-muted mb-0">
                                      {serviceOrder?.company_name}
                                      {serviceOrder?.company_name && serviceOrder?.trn_no && <br />}
                                      {serviceOrder?.trn_no && `TRN: ${serviceOrder?.trn_no}`}
                                    </p>
                                  )}
                                </td>
                                <td>
                                  <div
                                    className="text-truncate"
                                    style={{ maxWidth: 200 }}
                                    title={serviceOrder?.address1 || ''}
                                  >
                                    {serviceOrder?.address1 || '-'}
                                  </div>
                                </td>
                                <td>
                                  <div>
                                    {serviceOrder?.area
                                      ? serviceOrder?.area
                                      : '-'}
                                  </div>
                                  {serviceOrder?.emirate && (
                                    <small className="text-muted">
                                      {serviceOrder?.emirate}
                                    </small>
                                  )}
                                </td>
                                <td>
                                  <div className="text-capitalize">
                                    {serviceOrder?.service_type
                                      ? serviceOrder.service_type.replace(
                                          /_/g,
                                          ' ',
                                        )
                                      : '-'}
                                  </div>
                                  {serviceOrder?.product_type && (
                                    <small className="text-muted">
                                      <p className="mb-0">
                                        {serviceOrder?.product_type}
                                      </p>
                                    </small>
                                  )}

                                  {serviceOrder?.model_no && (
                                    <small className="text-muted">
                                      <p className="mb-0">
                                        Model :<b>{serviceOrder?.model_no}</b>
                                      </p>
                                    </small>
                                  )}
                                </td>
                                <td nowrap="nowrap">
                                  <div className="fs-12 d-flex justify-content-between gap-3 mb-1">
                                    <span>Sub Total:</span>
                                    <b>
                                      {Number(
                                        serviceOrder?.sub_total ?? 0,
                                      ).toFixed(2)}
                                    </b>
                                  </div>
                                  <div className="fs-12 d-flex justify-content-between gap-3 mb-1">
                                    <span>Discount:</span>
                                    <b>
                                      {Number(
                                        serviceOrder?.discount_total ?? 0,
                                      ).toFixed(2)}
                                    </b>
                                  </div>
                                  <div className="fs-12 d-flex justify-content-between gap-3">
                                    <span>Order Total:</span>
                                    <b>
                                      {Number(
                                        serviceOrder?.order_total ?? 0,
                                      ).toFixed(2)}
                                    </b>
                                  </div>
                                  {(() => {
                                    let info = serviceOrder?.payment_info;
                                    if (typeof info === 'string') {
                                      try {
                                        info = JSON.parse(info);
                                      } catch (e) {
                                        info = null;
                                      }
                                    }
                                    if (!info) return null;
                                    const provider = info.provider;
                                    const providerId = info.paymentProviderId;
                                    if (!provider && !providerId) return null;
                                    return (
                                      <div className="fs-12 mt-1 pt-1 border-top">
                                        <b>
                                          {provider}
                                          {provider && providerId && (
                                            <span className="mx-2 text-muted fw-normal">
                                              |
                                            </span>
                                          )}
                                          {providerId}
                                        </b>
                                      </div>
                                    );
                                  })()}
                                </td>
                                <td>
                                  {(() => {
                                    const status =
                                      serviceOrder?.status_label?.toLowerCase() ||
                                      '';
                                    let badgeColor =
                                      'bg-soft-light text-dark border-light';
                                    let iconClass =
                                      'mdi mdi-information-outline';

                                    if (status === 'new') {
                                      badgeColor = 'bg-soft-info text-info';
                                      iconClass = 'mdi mdi-star-circle-outline';
                                    } else if (
                                      status === 'canceled' ||
                                      status === 'closed'
                                    ) {
                                      badgeColor = 'bg-soft-danger text-danger';
                                      iconClass =
                                        'mdi mdi-close-circle-outline';
                                    } else if (
                                      status === 'assigned to technician' ||
                                      status === 'in progress'
                                    ) {
                                      badgeColor = 'bg-soft-info text-info';
                                      iconClass = 'mdi mdi-progress-clock';
                                    } else if (status === 'resolved') {
                                      badgeColor =
                                        'bg-soft-success text-success';
                                      iconClass =
                                        'mdi mdi-check-circle-outline';
                                    } else if (status === 'rescheduled') {
                                      badgeColor =
                                        'bg-soft-warning text-warning';
                                      iconClass = 'mdi mdi-calendar-sync';
                                    }

                                    return (
                                      <div className="d-flex flex-column align-items-start">
                                        <div
                                          style={{
                                            borderColor:
                                              'var(--bs-border-color)',
                                          }}
                                          className={`badge fs-12 px-2 py-1 text-capitalize ${badgeColor} border d-inline-flex align-items-center gap-1`}
                                        >
                                          <i
                                            className={`${iconClass} fs-14`}
                                          ></i>
                                          {serviceOrder?.status_label}
                                        </div>
                                        {status === 'resolved' &&
                                          resolvedDateTime && (
                                            <div
                                              className="mt-1 d-flex align-items-center gap-1 text-muted ms-1"
                                              style={{ fontSize: '11px' }}
                                            >
                                              <i className="mdi mdi-calendar-check text-success"></i>
                                              <span>On {resolvedDateTime}</span>
                                            </div>
                                          )}
                                        {status === 'pending' &&
                                          lastScheduledDateTime && (
                                            <div
                                              className="mt-1 d-flex align-items-center gap-1 text-muted ms-1"
                                              style={{ fontSize: '11px' }}
                                            >
                                              <i className="mdi mdi-calendar-check text-warning"></i>
                                              <span>
                                                Last Scheduled <br />
                                                On {lastScheduledDateTime}
                                              </span>
                                            </div>
                                          )}
                                        {status === 'rescheduled' &&
                                          rescheduledDateTime && (
                                            <div
                                              className="mt-1 d-flex align-items-center gap-1 text-muted ms-1"
                                              style={{ fontSize: '11px' }}
                                            >
                                              <i className="mdi mdi-calendar-sync text-warning"></i>
                                              <span>
                                                Rescheduled <br />
                                                To {rescheduledDateTime}
                                              </span>
                                            </div>
                                          )}
                                      </div>
                                    );
                                  })()}
                                </td>
                                <td>
                                  <span className="fs-12">
                                    {serviceOrder?.technician_name} -{' '}
                                    {serviceOrder?.emp_id}
                                  </span>
                                  <p className="mb-1">{serviceOrder?.phone}</p>
                                  {serviceOrder?.scheduled_date && (
                                    <small className="text-muted">
                                      On :{' '}
                                      {moment(
                                        new Date(serviceOrder.scheduled_date),
                                      ).format('DD-MM-YYYY')}
                                    </small>
                                  )}
                                </td>
                                <td>{serviceOrder?.created_by || '-'}</td>
                                <td>
                                  <div className="action-flex-container">
                                    <span
                                      className="btn btn-sm btn-outline-warning waves-effect waves-light"
                                      title="View Complaint Info"
                                      onClick={() => {
                                        // Open the new page with the required URL
                                        if (serviceOrder?.complaint_no) {
                                          window.open(
                                            `/service-orders/manage-service-orders/${serviceOrder.complaint_no}`,
                                            '_blank',
                                          );
                                        }
                                      }}
                                    >
                                      <i className="mdi mdi-eye-outline"></i>
                                    </span>
                                  </div>
                                </td>
                              </tr>
                            </tbody>
                          );
                        })}
                      </>
                    ) : (
                      <>
                        <tbody>
                          <tr>
                            <td colSpan={11}>No Records found</td>
                          </tr>
                        </tbody>
                      </>
                    )}
                  </Table>
                </div>
                {serviceOrderData?.length > 0 ? (
                  <DHPagination
                    totalPages={listOrders?.recordsTotal}
                    currentPage={paginationValue.page}
                    setPaginationValue={setPaginationValue}
                    setLimitValue={setLimitValue}
                  />
                ) : null}
              </div>
            )}
          </Col>
        </Row>
      </>
    </>
  );
};

export default ManageServiceOrdersList;
